import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { Payline } from "../GameComponents/Payline";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ComponentTypes } from "../Components/ComponentTypes";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { Tweens } from "../Components/Effects/Tweens";
import { MachineConfigurationAttributes, ReelConfigurationAttributes } from "../Components/Configuration";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { Settings } from "./Settings";
import { ISymbolTeasingAttributes, SymbolTeasingActions, TeasingComponent } from "./TeasingComponent";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { RopeElement } from "./RopeElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../Components/StageController";
import { Symbols } from "../GameComponents/Symbols";
import { Paylines } from "../GameComponents/Paylines";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

/**
 * WinAnimatorExtended extends WinAnimator which uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimatorExtended
 * @extends WinAnimator
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    protected elementInstance: Array<Object> = [];

    // pre roll height in pixels
    private _preRoll: number = 180;
    // pre roll speed in frames
    private _preRollSpeed: number = 10;
    // delay to start rotation in frames
    private _startDelay: number = 3;

    private _delayCounter: number = 120; // delay to simulate counter animation if win is zero
    private _delayCycle: number = 90; // between individual cycles
    private _delayCycleFinal: number = 90; // after last cycle
    private _delayIdle: number = 90; // idle
    private _delayIdleInitial: number = 90; // first idle

    private _customData: Object = {};

    private cycleAnimation: TimelineElement;
    private subservientCycleSymbols: Array<number> = [9]; // symbols will attempt to go to different CYCLE_N states according to other symbols in given win line

    private starTimerStack: Array<FrameTimerEventEmitter> = [];

    public setCustomData(value: any) {
        this._customData = value;
    }

    protected currentValue: number;
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly StartFreeSpins: string = "startFreespins";

    public static readonly WonNewFreeSpins: string = "wonNewFreespins";

    public static readonly StartConfigurator: string = "startConfigurator";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    protected static readonly ShowWinsWithoutBonusSymbolWin: string = "showWinsWithoutBonusSymbolWin";
    protected static readonly StartAnimation: string = "startAnimation";
    protected static readonly CompleteAnimation: string = "completeAnimation"; // for faking score animation with scatters

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    protected configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)) {
            this._preRoll = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL));
        }

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)) {
            this._preRollSpeed = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED));
        }

        if (node.hasAttribute(MachineConfigurationAttributes.START_DELAY)) {
            this._startDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.START_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.DELAY_COUNTER)) {
            this._delayCounter = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.DELAY_COUNTER));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.DELAY_CYCLE)) {
            this._delayCycle = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.DELAY_CYCLE));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.DELAY_CYCLE_FINAL)) {
            this._delayCycleFinal = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.DELAY_CYCLE_FINAL));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.DELAY_IDLE)) {
            this._delayIdle = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.DELAY_IDLE));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.DELAY_IDLE_INITIAL)) {
            this._delayIdleInitial = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.DELAY_IDLE_INITIAL));
        }
    }

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (this.childs["Cycle"]) {
            this.cycleAnimation = this.childs["Cycle"];
            this.cycleAnimation.visible = false;
            this.cycleAnimation.on("finished", this.onCycleAnimationFinished.bind(this));
            this.cycleAnimation.on(TimelineElement.AnimationComplete, this.onCycleAnimationComplete.bind(this));
            this.animationsContainer.addChild(this.cycleAnimation);
        }
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number, force: boolean = false): void {
        //super.onReelStopped(reelsData, reelIndex);

        this.currentReels = reelsData;

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            // for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentSymbol(reelIndex, i);
        }

        if (this.elementInstance["teasing"] != null) {
            let sta: Array<Array<ISymbolTeasingAttributes>>;
            /*if (this.elementInstance['teasing'] != null) {
                this.elementInstance['teasing'].forEach(function(teasingComponent: TeasingComponent) {
                    sta = teasingComponent.checkSymbolsPerReel(reelIndex);
                }, this);
            }*/
            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponent) {
                    let symbolsTeasingAttributes = teasingComponent.checkSymbolsPerReel(reelIndex);
                    sta = symbolsTeasingAttributes;
                }, this);
            }

            //let tc: TeasingComponent = this.elementInstance['teasing'];
            //if (tc.teasingEnabled) {
            //let sta: Array<Array<ISymbolTeasingAttributes>> = tc.checkSymbolsPerReel(reelIndex);

            if (!force) {
                for (let i = 0; i <= reelIndex; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        if (this.currentSymbols[i][j] != null) {
                            if (typeof sta !== "undefined") {
                                if (sta[i][j].action === SymbolTeasingActions.NOWIN) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                                    }
                                } else if (sta[i][j].action === SymbolTeasingActions.NORMAL) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                    }
                                } else {
                                    if (i == reelIndex) {
                                        if (sta[i][j].action === SymbolTeasingActions.IMPACT_NOWIN) {
                                            if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactNowin)) {
                                                this.currentSymbols[i][j].goToCase(Symbol.CaseImpactNowin);
                                            }
                                        } else if (sta[i][j].action === SymbolTeasingActions.IMPACT) {
                                            if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                                this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                            }
                                        } else if (sta[i][j].action === SymbolTeasingActions.TEASE) {
                                            if (this.allowSymbolGoToCase(i, j, Symbol.CaseTeaser)) {
                                                this.currentSymbols[i][j].goToCase(Symbol.CaseTeaser);
                                            }
                                        } else if (sta[i][j].action === SymbolTeasingActions.IMPACT_TEASE) {
                                            if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactTease)) {
                                                this.currentSymbols[i][j].goToCase(Symbol.CaseImpactTease);
                                            }
                                        }
                                    }
                                }
                            } else {
                                if (i == reelIndex && this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                } else if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            } else {
                // animation dice
                for (let i = 0; i <= reelIndex; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        if (this.currentSymbols[i][j] != null) {
                            if (typeof sta !== "undefined") {
                                if (sta[i][j].action === SymbolTeasingActions.IMPACT) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                    }
                                } else if (i == reelIndex && sta[i][j].action === SymbolTeasingActions.IMPACT_TEASE) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactTease)) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseImpactTease);
                                    }
                                }
                            } else {
                                if (i == reelIndex && this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                } else if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    /**
     *
     * @param {Array<"neo-games/Engine/Communication/Game/Types/IWin".IWin>} wins
     * @param {number} win
     * @returns {boolean}
     */
    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        if (wins == null || wins.length === 0) {
            this.startStarAnimation();
        }

        return super.showWins(wins, win);
    }

    /**
     * Show first phase wins.
     */
    protected showFirstWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);
        let symbols: Array<Array<Symbol>> = [];

        if (!ModuleManager.freespinsActive()) {
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                //this.winPaylineGoToCase(i, Payline.CaseWin);
            }
            if (this.currentWin > 0) {
                this.startAnimation();
            } else {
                // delays instead of showing counter animation
                this.startTimer(WinAnimatorExtended.CompleteAnimation, this._delayCounter);
            }
        } else {
            let hasScatterFreespinWin: boolean = false;
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                    if (this.currentWins[i].type === Communication.Game.WinType.freeSpins) {
                        hasScatterFreespinWin = true;
                        symbols.push(this.winSymbolsGoToCase(i, Symbol.CaseWin, false));
                        this.winPaylineGoToCase(i, Payline.CaseWin);
                    }
                }
            }

            if (hasScatterFreespinWin) {
                this.startTimer(WinAnimatorExtended.ShowWinsWithoutBonusSymbolWin, this._delayCycle);
                this.showLineToCounter(symbols, Settings.SymbolTeasing);
            } else {
                this.showWinsWithoutBonusSymbolWin();
            }
        }
    }

    /**
     *
     * @param {number} winIndex
     * @param {string} caseType
     * @returns {Array<Symbol>}
     */
    protected winSymbolsGoToCaseAsCustom(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    /**
     * Shows wins without bonus symbol win.
     */
    public showWinsWithoutBonusSymbolWin(): void {
        let symbols: Array<Array<Symbol>> = [];
        for (let i = 0; i < this.currentWins.length; i++) {
            if (
                this.currentWins[i].trigger !== Communication.Game.WinTrigger.Scatter &&
                this.currentWins[i].type !== Communication.Game.WinType.freeSpins
            ) {
                symbols.push(this.winSymbolsGoToCase(i, Symbol.CaseWin, false));
                this.winPaylineGoToCase(i, Payline.CaseWin);
            }
        }

        this.startAnimation();
    }

    /**
     *
     * @param {Array<Array<Symbol>>} symbols
     * @param {number} symbolId
     */
    private showLineToCounter(symbols: Array<Array<Symbol>>, symbolId: number): void {
        let ropeSwitch: SwitchElement = this.elementInstance["ropeSwitch"];
        let flareSwitch: SwitchElement = this.elementInstance["flareSwitch"];

        let scale = StageController.Width / 1920;

        if (symbols != null && (ropeSwitch != null || flareSwitch != null)) {
            let points: Array<any> = [];

            for (let i: number = 0; i < symbols.length; i++) {
                for (let j: number = 0; j < symbols[i].length; j++) {
                    if (symbols[i][j].id == symbolId) {
                        let point = {
                            x: symbols[i][j].getGlobalPosition().x + 144 * scale,
                            y: symbols[i][j].getGlobalPosition().y + 144 * scale,
                        };
                        if (!this.containPoint(points, point)) {
                            points.push(point);
                        }
                    }
                }
            }

            // if (points.length > 0) {
            //     points.sort(function(a, b) {
            //         if (a.x < b.x) {
            //             return -1;
            //         } else if (a.x > b.x) {
            //             return 1;
            //         } else {
            //             return 0;
            //         }
            //     });
            //
            //     if (ropeSwitch != null) {
            //         let ropeElement = <RopeElement>ropeSwitch.getChildElementByName("Rope");
            //         if (ropeElement != null) {
            //             ropeElement.addPoints(points);
            //             ropeSwitch.goToCase("SHOW_SYMBOL_" + symbolId);
            //             points = ropeElement.getPoints();
            //             PlayList.play('SND_FSeyeline01');
            //         }
            //     }
            //
            //     if (flareSwitch != null) {
            //         let flareElement = <ImageElement>flareSwitch.getChildElementByName("Flare");
            //         if (flareElement != null) {
            //             flareSwitch.goToCase("SHOW_SYMBOL_" + symbolId);
            //
            //             flareElement.position.x = points[0].x;
            //             flareElement.position.y = points[0].y;
            //             Tweens.positionTween(flareElement, {
            //                 bezier: {
            //                     values: points
            //                 },
            //                 tweenX: [flareElement.x, flareElement.x],
            //                 tweenY: [flareElement.y, flareElement.y],
            //                 tweenDuration: 60,
            //                 tweenDelay: 60
            //             });
            //         }
            //     }
            // }
        }
    }

    private containPoint(points: Array<any>, newPoint: any): boolean {
        for (let i: number = 0; i < points.length; i++) {
            if (points[i].x == newPoint.x && points[i].y == newPoint.y) {
                return true;
            }
        }
        return false;
    }

    /**
     *
     */
    protected finishFirstWins(): void {
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWinEnd, false);
        }

        this.emit(WinAnimator.FinishWinAnimator);

        // Cycle wins
        this.currentCycle = 0;
        this.cycleWins();
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    /**
     *
     * @param {Array<"neo-games/Engine/Communication/Game/Types/IWin".IWin>} wins
     * @param {number} win
     */
    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        if (!ModuleManager.freespinsActive()) {
            AnimationElement.DynamicTint = "ffff00";
        } else {
            AnimationElement.DynamicTint = "ffffff";
        }

        super.showInitWins(wins, win);
    }

    /**
     * Overrides method showFirstInitWins() because Symbol goes to CaseWinEnd.
     */
    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWinEnd, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }
    }

    /**
     *
     */
    protected finishFirstInitWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;
        this.cycleWins();
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    /**
     *
     */
    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        if (!ModuleManager.freespinsActive()) {
            AnimationElement.DynamicTint = "ffff00";
        } else {
            AnimationElement.DynamicTint = "ffffff";
        }

        let skipCycle: boolean = false;
        if (
            this.currentWins[this.currentCycle].trigger === Communication.Game.WinTrigger.Scatter &&
            this.currentWins[this.currentCycle].type === Communication.Game.WinType.freeSpins
        ) {
            this.allSymbolsGoToCase(Symbol.CaseNormal, true);
            skipCycle = true;
        } else {
            PlayList.play("SND_BaseRecapWinline");

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, true);
            }
            this.playSoundBasedOnPayout(this.currentWins[this.currentCycle].payoutId);
            let shownSymbols = this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            let masterSymbol: number = 0;
            for (let i = 0; i < shownSymbols.length; i++) {
                if (this.subservientCycleSymbols.indexOf(shownSymbols[i].id) == -1) {
                    masterSymbol = shownSymbols[i].id;
                    break;
                }
            }
            if (masterSymbol) {
                shownSymbols.forEach((symbol) => {
                    if (this.subservientCycleSymbols.indexOf(symbol.id) != -1) {
                        // symbol.hasCase(Symbol.CaseCycle+"_"+masterSymbol);
                        // symbol.goToCase(Symbol.CaseCycle+"_"+masterSymbol);
                        symbol.goToCase(Symbol.CaseCycle);
                    }
                });
            }
            if (shownSymbols[0] && this.cycleAnimation && this.currentWins[this.currentCycle].amount) {
                this.cycleAnimation.position.x = shownSymbols[0].position.x;
                this.cycleAnimation.position.y = shownSymbols[0].position.y;
                this.cycleAnimation.visible = true;
                if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                    ImageTextCounterElement.Round = true;
                    this.cycleAnimation.play(
                        parseInt(
                            NumberUtils.devide(
                                this.currentWins[this.currentCycle].amount,
                                BetManager.CoinSize,
                            ).toString(),
                        ),
                    );
                } else {
                    ImageTextCounterElement.Round = false;
                    this.cycleAnimation.play(this.currentWins[this.currentCycle].amount);
                }
            }
        }

        this.currentCycle = this.currentCycle + 1;

        if (this.currentCycle >= this.currentWins.length) {
            this.currentCycle = 0;
        }

        if (skipCycle) {
            this.startTimer(WinAnimator.CycleWins, 1);
        } else {
            this.startTimer(WinAnimator.CycleWins, this.currentCycle === 0 ? this._delayCycleFinal : this._delayCycle);
        }
    }

    private onCycleAnimationFinished(): void {
        this.cycleAnimation.visible = false;
    }

    private onCycleAnimationComplete(): void {
        this.cycleAnimation.visible = false;
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected startAnimation(): void {
        super.startAnimation();
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        this.stopStarAnimation();

        if (this.cycleAnimation) {
            this.cycleAnimation.visible = false;
        }
        if (spin) {
            this.allSymbolsGoToCase(Symbol.CaseNormal, true);
            for (let reelIndex = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                for (let symbolIndex = 0; symbolIndex < this.currentSymbols[reelIndex].length; symbolIndex++) {
                    this.removeCurrentSymbol(reelIndex, symbolIndex);
                }
            }
        }
        if (showLastSymbols) {
            this.startStarAnimation();
        }

        return super.reset(showLastSymbols);
    }

    /**
     *
     */
    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowWinsWithoutBonusSymbolWin:
                this.showWinsWithoutBonusSymbolWin();
                break;
            case WinAnimatorExtended.StartAnimation:
                this.startAnimation();
                break;
            case WinAnimatorExtended.CompleteAnimation:
                this.onAnimationComplete();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    /**
     *
     * @param {number} reelIndex
     * @param {number} symbolIndex
     */
    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);
        this.sortChildrenByZindex();
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        //super.allSymbolsGoToCase(caseType, noWin);
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                // for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }
            }
        }

        this.sortChildrenByZindex();
    }

    /**
     *
     * @param {number} winIndex
     * @param {string} caseType
     * @param {boolean} useValue
     */
    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        let symbol: Array<Symbol> = super.winSymbolsGoToCase(winIndex, caseType, useValue);
        this.sortChildrenByZindex();

        return symbol;
    }

    /**
     * Sorts children of container by symbols zIndex attribute.
     */
    protected sortChildrenByZindex(): void {
        this.symbolsContainer.children.sort((a: any, b: any) => {
            if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                a.zIndex = a.zIndex || 0;
                b.zIndex = b.zIndex || 0;

                return a.zIndex - b.zIndex;
            }

            return 0;
        });
    }

    /**
     *
     * @param {number} payoutId
     */
    private playSoundBasedOnPayout(payoutId: number = -1): void {}

    /**
     *
     * @param {number} reelIndex
     * @param {number} symbolIndex
     * @param {string} caseType
     * @returns {boolean}
     */
    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        if (
            this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle &&
            caseType === Symbol.CaseNormal
        ) {
            return false;
        }

        if (
            this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseImpactNowin &&
            caseType === Symbol.CaseNowin
        ) {
            return false;
        }

        if (
            this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseImpact &&
            caseType === Symbol.CaseNormal
        ) {
            return false;
        }

        if (
            this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseImpactTease &&
            caseType === Symbol.CaseNormal
        ) {
            return false;
        }

        return true;
    }

    public getCurrentMultiplierIndex(): number {
        return this.currentMultiplierIndex;
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************
    protected idle(): void {
        super.idle();
    }

    protected getIdleDelay(): number {
        return this._delayIdle;
    }

    protected getInitialIdleDelay(): number {
        return this._delayIdleInitial;
    }

    protected getIdleActiveElementsCount(): number {
        return 3;
    }

    //******************************************************************************************************************
    //  STAR ANIMATION
    //******************************************************************************************************************

    showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        //super.showSymbols(reelsData);
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData.length; i++) {
            for (let j = 0; j < reelsData[i].symbols.length; j++) {
                this.removeCurrentSymbol(i, j);
            }
        }

        for (let i = 0; i < reelsData.length; i++) {
            for (let j = 0; j < reelsData[i].symbols.length; j++) {
                //     for (let j = reelsData[i].symbols.length - 1; j >= 0; j--) {
                this.addCurrentSymbol(i, j);
            }
        }

        this.startStarAnimation();
    }

    private startStarAnimation(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            let row = this.currentSymbols[i];
            for (let j = 0; j < row.length; j++) {
                let timer = CreateFrameTimer("symbol" + i + j);
                this.starTimerStack.push(timer);
                let rand = Math.floor(Math.random() * 15) + 1;
                timer.start(rand * 45);
                timer.on(FrameTimerEvents.Complete, () => {
                    let symbol = row[j] as Symbol;
                    let star = symbol.getChildElementByName("Star") as TimelineElement;
                    if (star != null) {
                        star.play();
                    }
                });
            }
        }
    }

    private stopStarAnimation(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            let row = this.currentSymbols[i];
            for (let j = 0; j < row.length; j++) {
                let symbol = row[j] as Symbol;
                let star = symbol.getChildElementByName("Star") as TimelineElement;
                if (star != null) {
                    star.play();
                }
            }
        }

        for (let i = 0, len = this.starTimerStack.length; i < len; i++) {
            let timer = this.starTimerStack.pop();
            timer.stop();
        }
    }
}

export const WinAnimatorExtendedAttributes = {
    DELAY_COUNTER: "DelayCounter",
    DELAY_CYCLE: "DelayCycle",
    DELAY_CYCLE_FINAL: "DelayCycleFinal",
    DELAY_IDLE: "DelayIdle",
    DELAY_IDLE_INITIAL: "DelayIdleInitial",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/